import { type ElementType, useId } from 'react';

import { ClockIcon } from '@heroicons/react/24/outline';
import { differenceInCalendarDays } from 'date-fns';

import { defaultCommunityTranslate } from '../utils/defaultCommunityTranslate';

import { ComposerFrame, ComposerFrameBody, ComposerFrameHeader } from './_components/layout/ComposerFrame';
import { type CommunityScheduledQueuePost, ScheduledQueueRow } from './_components/scheduledQueue/ScheduledQueueRow';
import type { CommunitySchedulePickerTranslate } from './_components/schedulePicker/SchedulePickerFrame';

interface CommunityScheduledQueueProps<Post extends CommunityScheduledQueuePost> {
  /** The team's pending and failed posts, soonest first, as the scheduled posts index returns them; unset while they load. */
  posts?: Post[];
  /** Shown as "You" on their own posts. */
  currentMemberId?: string;
  isAdmin?: boolean;
  onBack: () => void;
  /** Opens the schedule picker from an empty queue. */
  onSchedule: () => void;
  onEdit: (post: Post) => void;
  onReschedule: (post: Post) => void;
  onPublishNow: (post: Post) => void;
  onTakeOver: (post: Post) => void;
  onDelete: (post: Post) => void;
  locale?: string;
  translate?: CommunitySchedulePickerTranslate;
  /** Renders the visible title, an h2 by default. A host's dialog title component names its dialog after it. */
  titleAs?: ElementType;
}

const KEY = 'community.composer.schedule.queue';

// Stable, so the title takes the focus once, when it mounts.
const focusOnMount = (element: HTMLElement | null) => element?.focus();

// The whole team's scheduled posts, in the composer's dialog like the schedule picker. Each post's menu offers what the
// member viewing it can do; the host confirms and sends each action. Like the picker, it has no close button: the arrow
// goes back to the picker.
export const CommunityScheduledQueue = <Post extends CommunityScheduledQueuePost>({
  posts,
  onBack,
  onSchedule,
  locale = 'en',
  translate = defaultCommunityTranslate,
  titleAs: Title = 'h2',
  ...rowProps
}: CommunityScheduledQueueProps<Post>) => {
  const titleId = useId();

  return (
    // Named after the title, which takes the focus, so a screen reader says where the focus went when the queue
    // replaces the picker.
    <ComposerFrame role="group" aria-labelledby={titleId}>
      <ComposerFrameHeader onBack={onBack} backLabel={translate('shared.cta.back')}>
        <Title
          id={titleId}
          ref={focusOnMount}
          tabIndex={-1}
          className="truncate text-[15px] font-semibold leading-5 text-black focus:outline-none"
        >
          {translate(`${KEY}.title`)}
          {posts?.length ? <span className="ml-1 font-normal text-black/40"> {posts.length}</span> : null}
        </Title>
      </ComposerFrameHeader>
      <ComposerFrameBody className="gap-4 overflow-y-auto">
        {posts?.length ? (
          <>
            <p className="text-[13px] leading-4 text-black/50">{translate(`${KEY}.description`)}</p>
            {groupByDay(posts, locale).map((group) => (
              <DayGroup
                key={group.label}
                label={group.label}
                posts={group.posts}
                locale={locale}
                translate={translate}
                {...rowProps}
              />
            ))}
          </>
        ) : null}
        {posts?.length === 0 ? <EmptyQueue onSchedule={onSchedule} translate={translate} /> : null}
      </ComposerFrameBody>
    </ComposerFrame>
  );
};

// "Today", "Tomorrow" and "Yesterday" near now, else "Monday, Oct 5", capitalized since some languages lowercase them.
const dayLabel = (date: Date, now: Date, locale: string) => {
  const days = differenceInCalendarDays(date, now);
  const label =
    Math.abs(days) <= 1
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day')
      : new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'short', day: 'numeric' }).format(date);

  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
};

const groupByDay = <Post extends CommunityScheduledQueuePost>(posts: Post[], locale: string) => {
  const now = new Date();
  const groups: { label: string; posts: Post[] }[] = [];
  for (const post of posts) {
    const label = dayLabel(new Date(post.scheduled_at), now, locale);
    const group = groups.at(-1);
    if (group?.label === label) group.posts.push(post);
    else groups.push({ label, posts: [post] });
  }

  return groups;
};

const DayGroup = <Post extends CommunityScheduledQueuePost>({
  label,
  posts,
  currentMemberId,
  isAdmin = false,
  locale,
  translate,
  onEdit,
  onReschedule,
  onPublishNow,
  onTakeOver,
  onDelete,
}: Omit<CommunityScheduledQueueProps<Post>, 'onBack' | 'onSchedule' | 'titleAs' | 'posts'> & {
  label: string;
  posts: Post[];
  locale: string;
  translate: CommunitySchedulePickerTranslate;
}) => {
  const headingId = useId();

  return (
    <section className="flex flex-col gap-2">
      <h3 id={headingId} className="text-[13px] font-medium leading-4 text-black/50">
        {label}
      </h3>
      <ul aria-labelledby={headingId} className="flex flex-col gap-2">
        {posts.map((post) => (
          <li key={post.id}>
            <ScheduledQueueRow
              post={post}
              isAuthor={Boolean(currentMemberId) && post.author.id === currentMemberId}
              isAdmin={isAdmin}
              locale={locale}
              translate={translate}
              onEdit={() => onEdit(post)}
              onReschedule={() => onReschedule(post)}
              onPublishNow={() => onPublishNow(post)}
              onTakeOver={() => onTakeOver(post)}
              onDelete={() => onDelete(post)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

const EmptyQueue = ({
  onSchedule,
  translate,
}: {
  onSchedule: () => void;
  translate: CommunitySchedulePickerTranslate;
}) => (
  <div className="m-auto flex max-w-xs flex-col items-center gap-2 py-12 text-center">
    <span className="mb-2 flex size-12 items-center justify-center rounded-full bg-black/5 text-black/40">
      <ClockIcon className="size-6" aria-hidden="true" />
    </span>
    <p className="text-[15px] font-semibold leading-5 text-black">{translate(`${KEY}.empty_title`)}</p>
    <p className="text-[13px] leading-4 text-black/50">{translate(`${KEY}.empty_description`)}</p>
    <button
      type="button"
      onClick={onSchedule}
      className="mt-4 inline-flex h-10 items-center justify-center rounded-full bg-[var(--community-primary,#000)] px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] text-[var(--community-text-on-primary,#fff)] transition-opacity hover:opacity-90"
    >
      {translate(`${KEY}.schedule_post`)}
    </button>
  </div>
);
